<!--
  The one thing the account needs to hear, on every dashboard page.

  A page nobody visits cannot warn anyone, so the warning comes to them. It shows at most one
  message, chosen in copy.js, and the ones that mean something is blocked have no close
  button at all rather than a hidden one.
-->
<template>
  <div v-if="banner && !hidden" class="ub" :class="banner.tone" :role="role">
    <div class="ub-body">
      <p class="ub-title">{{ banner.title }}</p>
      <p v-if="banner.text" class="ub-text">{{ banner.text }}</p>
    </div>

    <div class="ub-actions">
      <button
        v-for="a in banner.actions"
        :key="a.label"
        type="button"
        class="ub-btn"
        :class="{ primary: a === banner.actions[0] }"
        :disabled="busy === a.label"
        @click="run(a)"
      >
        {{ busy === a.label ? 'Opening…' : a.label }}
      </button>
      <button
        v-if="banner.dismissible"
        type="button"
        class="ub-x"
        aria-label="Dismiss"
        @click="close"
      >
        ×
      </button>
    </div>
  </div>
</template>

<script>
import { subscribeAccount, currentAccount } from '@/http/requests/account';
import { openPortal } from '@/http/requests/billing';
import { bannerFor, isDismissed, dismiss } from '../account/copy';

export default {
  name: 'UsageBanner',
  data() {
    return {
      acc: currentAccount(),
      hidden: false,
      busy: '',
    };
  },
  computed: {
    banner() {
      return bannerFor(this.acc);
    },
    role() {
      return this.banner && this.banner.tone === 'bad' ? 'alert' : 'status';
    },
  },
  watch: {
    // a new situation is a new message, so an old dismissal must not silence it
    banner(next) {
      this.hidden = isDismissed(next);
    },
  },
  created() {
    this.unsubscribe = subscribeAccount((acc) => {
      this.acc = acc;
    });
    this.hidden = isDismissed(this.banner);
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    close() {
      dismiss(this.banner);
      this.hidden = true;
    },
    run(action) {
      if (action.action === 'portal') {
        this.busy = action.label;
        openPortal('/dashboard/account')
          .then((session) => {
            window.location.assign(session.url);
          })
          .catch(() => {
            this.busy = '';
          });
        return;
      }
      if (action.to && this.$route.path !== action.to)
        this.$router.push(action.to);
    },
  },
};
</script>

<style scoped>
.ub {
  font-family: 'JetBrains Mono', monospace !important;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px 16px;
  padding: 10px 14px;
  border-left: 3px solid #d7df23;
  background: #d7df2314;
  color: #e8e8c8;
}
.ub.warn {
  border-left-color: #f0c45a;
  background: #f0c45a14;
  color: #f4e2b0;
}
.ub.bad {
  border-left-color: #ef4444;
  background: #ef444414;
  color: #f4bdbd;
}
.ub-body {
  min-width: 0;
}
.ub-title {
  margin: 0;
  font-size: 13.5px;
  font-weight: 700;
  color: #fff;
}
.ub-text {
  margin: 2px 0 0;
  font-size: 12.5px;
  opacity: 0.9;
}
.ub-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.ub-btn {
  border: 1px solid #ffffff59;
  background: transparent;
  color: #fff;
  padding: 5px 12px;
  font-size: 12.5px;
  cursor: pointer;
  white-space: nowrap;
}
.ub-btn:hover {
  border-color: #fff;
}
.ub-btn.primary {
  background: #d7df23;
  border-color: #d7df23;
  color: #0a0b0f;
  font-weight: 700;
}
.ub.bad .ub-btn.primary {
  background: #ef4444;
  border-color: #ef4444;
  color: #fff;
}
.ub-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.ub-x {
  background: none;
  border: none;
  color: inherit;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.7;
  padding: 0 4px;
}
.ub-x:hover {
  opacity: 1;
}
@media (max-width: 767px) {
  .ub {
    align-items: flex-start;
  }
  .ub-actions {
    width: 100%;
  }
  .ub-btn {
    flex: 1;
    text-align: center;
  }
}
</style>
